import { useInfiniteQuery } from '@tanstack/react-query';
import { api, GrRewardLogEntry } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface UseRewardLogOptions {
  userId?: string;
  gameConfigId?: string;
}

interface UseRewardLogReturn {
  entries: GrRewardLogEntry[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  loadMore: () => void;
  hasMore: boolean;
}

const LIMIT = 20;

export function useRewardLog(options: UseRewardLogOptions = {}): UseRewardLogReturn {
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: queryKeys.gameRewards.rewardLog(),
    queryFn: ({ pageParam = 0 }) =>
      api.listGameRewardsLog({
        userId: options.userId || undefined,
        gameConfigId: options.gameConfigId || undefined,
        limit: LIMIT,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const nextOffset = (lastPageParam as number) + LIMIT;
      return nextOffset < lastPage.total ? nextOffset : undefined;
    },
  });

  const entries = data?.pages.flatMap((page) => page.entries) ?? [];
  const total = data?.pages[data.pages.length - 1]?.total ?? 0;

  return {
    entries,
    total,
    loading: isLoading,
    error: error instanceof Error ? error.message : isError ? 'Failed to load reward log' : null,
    refresh: () => {
      refetch();
    },
    loadMore: () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    hasMore: !!hasNextPage,
  };
}
